/**
 * Display helpers for the room detail page (D4c2).
 *
 * Pure functions, no React, no fetching — so every rule is testable on
 * its own and the page component stays an orchestrator (CLAUDE.md
 * Regel 48).
 *
 * **Nothing here derives occupancy.** ``planning_state``,
 * ``free_from_date``, ``current_occupancy`` and ``next_occupancy`` are
 * computed by the backend's canonical ``compute_room_planning`` and
 * arrive finished. These functions only choose German words and format
 * dates. A helper that compared ``move_in_date`` to today would be a
 * fifth answer to a question the codebase has already unified once —
 * which is exactly the drift this slice exists not to reintroduce.
 */
import { formatDateOnly } from "../../../utils/datetime";
import {
  ROOM_PLANNING_STATE_LABELS,
  ROOM_PLANNING_STATE_TONES,
} from "./roomConstants";

/** The page headline: the room's full label, else its bare name, else a
 * plain word. Never an id. */
export function roomTitle(room) {
  const display = (room?.display_name || "").trim();
  if (display) return display;
  const name = (room?.name || "").trim();
  return name || "Zimmer";
}

/** A date as "05.08.2026", or "—". ``formatDateOnly`` splits a
 * YYYY-MM-DD string without constructing a Date, so a civil date the
 * backend derived cannot shift by a timezone. */
export function roomDateText(value) {
  return value ? formatDateOnly(value) : "—";
}

/** German label for the backend's ``planning_state``, falling back to
 * the raw value so an unknown state is visible rather than blank. */
export function planningStateLabel(room) {
  const state = room?.planning_state;
  if (!state) return "Unbekannt";
  return ROOM_PLANNING_STATE_LABELS[state] || state;
}

/** Badge tone for the planning state; unknown states read neutral. */
export function planningStateTone(room) {
  return ROOM_PLANNING_STATE_TONES[room?.planning_state] || "slate";
}

/**
 * The one-line answer to "when is this room free?".
 *
 * Reads ``free_from_date`` — the FIRST FREE DAY — and says so. It never
 * renders a "belegt bis" from the same value: under FAHN inclusive end
 * dates those are different days, and the pair
 * ``current_occupancy.occupied_until`` / ``free_from_date`` already
 * carries both correctly.
 *
 * ``free_from_date`` null means open-ended, NOT unknown.
 */
export function availabilityText(room) {
  const state = room?.planning_state;
  if (!state) return "Keine Belegungsdaten";
  if (state === "free" || state === "free_then_reserved") return "Jetzt frei";
  if (!room?.free_from_date) return "Unbefristet belegt";
  return `Frei ab ${formatDateOnly(room.free_from_date)}`;
}

/** A tenant's name from an occupancy block, or an honest placeholder.
 * Never a UUID — the backend sends ``tenant_name`` null when it cannot
 * name the row, and null must stay a word. */
export function occupantName(occupancy) {
  return (occupancy?.tenant_name || "").trim() || "Unbekannter Mieter";
}

/**
 * "seit 01.01.2026", "01.01.2026 – 30.09.2026", "ab 01.10.2026".
 *
 * ``kind`` is "current" or "next", because the same two dates read
 * differently depending on whether the period has started.
 */
export function occupancyPeriodText(occupancy, kind) {
  if (!occupancy) return "—";
  const from = occupancy.move_in_date
    ? formatDateOnly(occupancy.move_in_date)
    : null;
  if (kind === "next") return from ? `ab ${from}` : "Datum offen";
  const until = occupancy.occupied_until
    ? formatDateOnly(occupancy.occupied_until)
    : null;
  if (from && until) return `${from} – ${until}`;
  if (from) return `seit ${from}`;
  if (until) return `bis ${until}`;
  return "—";
}

/** "18.5 m²" or null. Formatted with the Swiss locale so 1000.5 reads
 * as "1000.5" rather than "1,000.5". */
export function roomSizeText(room) {
  const raw = room?.size_m2;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString("de-CH")} m²`;
}

/** "CHF 1'250" — the room's PLANNED target rent (Soll). Never presented
 * as what anyone actually pays: the actual rent lives on the tenancy,
 * and conflating them is how a planning figure ends up read as revenue.
 * Zero is a real value and is shown as such. */
export function roomPriceText(room) {
  const n = Number(room?.price);
  if (!Number.isFinite(n)) return null;
  return `CHF ${n.toLocaleString("de-CH")}`;
}

/** "3 Monate" / "1 Monat", or "—".
 *
 * ``null`` must NOT become "0 Monate": ``Number(null)`` is 0, and
 * rendering that would state a notice period of zero months where the
 * truth is that none is recorded. Zero itself stays a real value. */
export function monthsText(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n} ${n === 1 ? "Monat" : "Monate"}`;
}
